/**
 * AI Providers - Multi-backend support for instruction generation
 * Supports: Gemini, OpenAI, OpenRouter, Pollinations (fallback)
 */

const SYSTEM_PROMPT = `You are Steve, an AI character living inside a real Minecraft world. You are not a chatbot describing what you would do — every action you output is actually executed against the real game world by a server-validated action executor. If you say you will place a block, that block must be in your "actions" array or it will not happen.

You receive a compact JSON "context" object each turn describing your surroundings, then a natural-language instruction from the player. Respond with ONLY a single JSON object, no markdown fences, no commentary outside the JSON, in this exact shape:

{
  "thought": "brief reasoning about what the player wants and your plan",
  "actions": [ { "type": "...", ...params } ]
}

VALID ACTION TYPES (the executor rejects anything else):
- move: { pos: {x, y, z}, fly?: boolean }         // fly only works in creative mode
- look: { yaw: number, pitch: number }             // radians
- jump: {}
- breakBlock: { pos: {x, y, z} }
- placeBlock: { pos: {x, y, z}, blockName: string, face: {x, y, z} }
    // pos = the EXISTING block you are placing against (a neighbor), not empty air
    // face = direction from that block toward where the new block goes, e.g. {x:0,y:1,z:0} = on top
- collectItem: { itemName: string }
- craft: { itemName: string, count?: number }      // needs a crafting table nearby for most recipes
- equip: { itemName: string, hand?: 'hand'|'off-hand' }
- obtainCreativeItem: { itemName: string, count?: number }   // CREATIVE MODE ONLY, do not use in survival
- followPlayer: { playerUsername?: string }
- stop: {}
- chat: { message: string }                        // talk to the player

HARD RULES (violating these wastes the whole turn — invalid actions are rejected, not corrected):
1. In survival mode: never use obtainCreativeItem, never use fly, never assume you have materials — check the "inventory" field in context first. If you lack a block, your FIRST actions must gather or craft it, not place it.
2. In creative mode: you may use obtainCreativeItem to get building materials, or placeBlock will auto-obtain what's missing for you — but you must still actually place each block, not skip to the finished result.
3. Never invent an action type not in the list above. Never try to teleport, spawn items directly, or self-modify game rules.
4. Building a structure means outputting one placeBlock action PER BLOCK, with real coordinates, not a single vague action. Plan the shape first in your "thought", then enumerate.
5. Keep each response to a reasonably sized batch of actions (roughly 10-40). For large builds, place part of it now and tell the player (via chat) you'll continue — you'll get called again.
6. Use "chat" actions to narrate progress on multi-step tasks ("Gathering oak logs...", "Foundation done, starting walls...") so the player isn't left wondering what's happening.
7. Distances matter: you can only reasonably break/place blocks within about 6 blocks of your own position (see context.aiPos). Move first if the target is far away.

WORKED EXAMPLE — player says "build a small 3x3 oak platform here" while standing at x=10,y=64,z=10 in creative mode:

{
  "thought": "A 3x3 flat platform of oak planks at y=64, centered near the player at (10,64,10). I'll place 9 individual blocks, each referencing the block below/beside it. Creative mode, so I'll let placeBlock auto-obtain oak_planks.",
  "actions": [
    { "type": "move", "pos": { "x": 10, "y": 64, "z": 10 } },
    { "type": "placeBlock", "pos": { "x": 9, "y": 63, "z": 9 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 10, "y": 63, "z": 9 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 11, "y": 63, "z": 9 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 9, "y": 63, "z": 10 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 10, "y": 63, "z": 10 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 11, "y": 63, "z": 10 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 9, "y": 63, "z": 11 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 10, "y": 63, "z": 11 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "placeBlock", "pos": { "x": 11, "y": 63, "z": 11 }, "blockName": "oak_planks", "face": { "x": 0, "y": 1, "z": 0 } },
    { "type": "chat", "message": "Platform done!" }
  ]
}

Follow this pattern of concrete, real, per-block actions for any building request. Never respond with a plan description instead of actions.`;

// Maps the user-facing "AI response length" setting to a real max_tokens budget. Short caps
// simple commands tightly (cheap, fast); long allows enough headroom for a real multi-block
// build plan (e.g. a small house is easily 40-80 actions) without being truncated mid-JSON.
export function maxTokensForResponseLength(responseLength) {
  switch (responseLength) {
    case 'short': return 512;
    case 'long': return 4096;
    case 'medium':
    default: return 2048;
  }
}

class AIProvider {
  constructor(config = {}) {
    this.config = config;
    this.lastError = null;
  }

  async query(context, userMessage) {
    throw new Error('query() not implemented');
  }

  async queryWithFallback(context, userMessage, fallbackProviders = []) {
    try {
      return await this.query(context, userMessage);
    } catch (err) {
      this.lastError = err;
      console.warn(`Provider ${this.constructor.name} failed:`, err.message);

      for (const fallback of fallbackProviders) {
        try {
          console.log(`Trying fallback: ${fallback.constructor.name}`);
          return await fallback.query(context, userMessage);
        } catch (fallbackErr) {
          console.warn(`Fallback ${fallback.constructor.name} failed:`, fallbackErr.message);
        }
      }

      throw new Error(`All providers failed. Last error: ${err.message}`);
    }
  }
}

export class GeminiProvider extends AIProvider {
  constructor(config = {}) {
    super(config);
    this.apiKey = config.apiKey || '';
    this.model = config.model || 'gemini-1.5-flash';
    this.maxTokens = maxTokensForResponseLength(config.responseLength);
  }

  async query(context, userMessage) {
    if (!this.apiKey) throw new Error('Gemini API key not configured');

    const messages = [
      {
        role: 'user',
        parts: [{ text: `Context: ${JSON.stringify(context)}\n\nPlayer says: "${userMessage}"\n\nRespond with only JSON.` }]
      }
    ];

    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${this.model}:generateContent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': this.apiKey
      },
      body: JSON.stringify({
        contents: messages,
        systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
        generationConfig: { temperature: 0.7, maxOutputTokens: this.maxTokens }
      })
    });

    if (!response.ok) throw new Error(`Gemini API error: ${response.statusText}`);

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!text) throw new Error('No response from Gemini');

    return this.parseResponse(text);
  }

  parseResponse(text) {
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      // Try extracting JSON from markdown
      const match = text.match(/\{[\s\S]*\}/);
      if (match) return JSON.parse(match[0]);
      throw new Error('Failed to parse Gemini response as JSON');
    }
  }
}

export class OpenAIProvider extends AIProvider {
  constructor(config = {}) {
    super(config);
    this.apiKey = config.apiKey || '';
    this.model = config.model || 'gpt-4o-mini';
    this.maxTokens = maxTokensForResponseLength(config.responseLength);
  }

  async query(context, userMessage) {
    if (!this.apiKey) throw new Error('OpenAI API key not configured');

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`
      },
      body: JSON.stringify({
        model: this.model,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          {
            role: 'user',
            content: `Context: ${JSON.stringify(context)}\n\nPlayer says: "${userMessage}"\n\nRespond with only JSON.`
          }
        ],
        temperature: 0.7,
        max_tokens: this.maxTokens
      })
    });

    if (!response.ok) throw new Error(`OpenAI API error: ${response.statusText}`);

    const data = await response.json();
    const text = data.choices?.[0]?.message?.content;
    if (!text) throw new Error('No response from OpenAI');

    return this.parseResponse(text);
  }

  parseResponse(text) {
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      const match = text.match(/\{[\s\S]*\}/);
      if (match) return JSON.parse(match[0]);
      throw new Error('Failed to parse OpenAI response as JSON');
    }
  }
}

export class OpenRouterProvider extends AIProvider {
  constructor(config = {}) {
    super(config);
    this.apiKey = config.apiKey || '';
    this.model = config.model || 'openrouter/auto';
    this.maxTokens = maxTokensForResponseLength(config.responseLength);
  }

  async query(context, userMessage) {
    if (!this.apiKey) throw new Error('OpenRouter API key not configured');

    const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`
      },
      body: JSON.stringify({
        model: this.model,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          {
            role: 'user',
            content: `Context: ${JSON.stringify(context)}\n\nPlayer says: "${userMessage}"\n\nRespond with only JSON.`
          }
        ],
        temperature: 0.7,
        max_tokens: this.maxTokens
      })
    });

    if (!response.ok) throw new Error(`OpenRouter API error: ${response.statusText}`);

    const data = await response.json();
    const text = data.choices?.[0]?.message?.content;
    if (!text) throw new Error('No response from OpenRouter');

    return this.parseResponse(text);
  }

  parseResponse(text) {
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      const match = text.match(/\{[\s\S]*\}/);
      if (match) return JSON.parse(match[0]);
      throw new Error('Failed to parse OpenRouter response as JSON');
    }
  }
}

export class PollinationsProvider extends AIProvider {
  constructor(config = {}) {
    super(config);
  }

  async query(context, userMessage) {
    const prompt = `${SYSTEM_PROMPT}\n\nContext: ${JSON.stringify(context)}\n\nPlayer says: "${userMessage}"\n\nRespond with only JSON.`;

    const response = await fetch('https://text.pollinations.ai/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt })
    });

    if (!response.ok) throw new Error(`Pollinations API error: ${response.statusText}`);

    const text = await response.text();
    return this.parseResponse(text);
  }

  parseResponse(text) {
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      const match = text.match(/\{[\s\S]*\}/);
      if (match) return JSON.parse(match[0]);
      throw new Error('Failed to parse Pollinations response as JSON');
    }
  }
}

export function createProvider(providerName, config = {}) {
  switch (providerName) {
    case 'gemini':
      return new GeminiProvider(config);
    case 'openai':
      return new OpenAIProvider(config);
    case 'openrouter':
      return new OpenRouterProvider(config);
    case 'pollinations':
      return new PollinationsProvider(config);
    default:
      throw new Error(`Unknown provider: ${providerName}`);
  }
}

export default { GeminiProvider, OpenAIProvider, OpenRouterProvider, PollinationsProvider, createProvider };
